import * as z from "zod";
import { pagination } from "../index";
import { shipmentStatuses } from "./enums";
import { getFulfillmentInput } from "./fulfillment";

// The shipments of a customer's orders as the customer reads them in Frontrow (P8.1f, ADR-220): what Tomorrow is
// preparing for it, what left, and where it is (FR-M13-02, I-11). Never the warehouse, the instruction to it, its
// acknowledgement or the notes of the Customer Service.

/**
 * Where a shipment of an order is, in the customer's words: `preparing` until anything of it leaves, `partly_shipped`
 * once a parcel left or was booked and the warehouse has still to close it, `shipped` once the warehouse closed it.
 */
export const customerFulfillmentStages = ["preparing", "partly_shipped", "shipped"] as const;
export type CustomerFulfillmentStage = (typeof customerFulfillmentStages)[number];

/** `listCustomerFulfillments`: the shipments of the customer's orders, the latest born first; never a cancelled one. */
export const listCustomerFulfillmentsInput = z.object({
  /** The shipments of one sales order: one per lot (ADR-195). */
  salesOrder: z.uuid().optional(),
  /** Part of the number of the shipment or of its sales order, case-insensitive and without separators (ADR-117). */
  q: z.string().trim().min(1).max(80).optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListCustomerFulfillmentsInput = z.input<typeof listCustomerFulfillmentsInput>;

/** `getCustomerFulfillment`: one shipment of the customer's orders; 404 for one of another customer (RLS). */
export const getCustomerFulfillmentInput = getFulfillmentInput;
export type GetCustomerFulfillmentInput = z.input<typeof getCustomerFulfillmentInput>;

/**
 * A parcel or a booking of the shipment, with its carrier and tracking: from the warehouse, booked by hand, or by the
 * TLG shipping platform (ADR-231).
 */
export const customerShipmentDto = z.object({
  id: z.uuid(),
  carrier: z.string().nullable(),
  service: z.string().nullable(),
  /** One for each parcel. */
  trackingNumbers: z.array(z.string()),
  packages: z.number().int().nullable(),
  /** When it left; null while it is only booked. */
  shippedAt: z.iso.datetime().nullable(),
  status: z.enum(shipmentStatuses),
  /** The page of the carrier that follows it, when the TLG shipping platform booked it (ADR-231); only `https`. */
  trackingUrl: z.url().nullable(),
  deliveredAt: z.iso.datetime().nullable(),
});

/** A shipment of an order as its customer sees it. */
export const customerFulfillmentDto = z
  .object({
    id: z.uuid(),
    fulfillmentNumber: z.string(),
    salesOrder: z.object({ id: z.uuid(), soNumber: z.string() }),
    /** The lot of the order it ships (ADR-195); null for an order locked before the lots. */
    lotNo: z.number().int().nullable(),
    stage: z.enum(customerFulfillmentStages),
    /** EXW: the customer's forwarder collects it, and Tomorrow books no carrier (FR-M13-01). */
    collectedByCustomer: z.boolean(),
    totalUnits: z.number().int(),
    /** Units that left so far (I-11). */
    shippedUnits: z.number().int(),
    /** When the warehouse closed it. */
    shippedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    /** Its parcels and bookings, the first one first. */
    shipments: z.array(customerShipmentDto),
  })
  .meta({ id: "CustomerFulfillment" });
export type CustomerFulfillmentDto = z.output<typeof customerFulfillmentDto>;
